import { Controller } from "@hotwired/stimulus"
import { get, post, put, patch, destroy } from '@rails/request.js'

export default class extends Controller {
  static targets = ["form", "cardElement", "submitButton", "buttonText", "loadingSpinner", "cardErrors"]
  static values = { 
    publishableKey: String,
    accountId: String,
    userEmail: String 
  }

  connect() {
    this.loadStripe()
  }

  disconnect() {
    if (this.cardElement) {
      this.cardElement.unmount()
    }
  }

  async loadStripe() {
    // Check if Stripe is already loaded
    if (window.Stripe) {
      this.initializeStripe()
      return
    }

    // Dynamically load Stripe if not already loaded
    try {
      await this.loadStripeScript()
      this.initializeStripe()
    } catch (error) {
      console.error('Failed to load Stripe:', error)
      this.showError('Failed to load payment processor. Please refresh the page and try again.')
    }
  }

  loadStripeScript() {
    return new Promise((resolve, reject) => {
      if (document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  initializeStripe() {
    // Initialize Stripe with publishable key
    this.stripe = Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements()

    // Create card element
    this.cardElement = this.elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: '#424770',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    })

    // Mount the card element
    this.cardElement.mount(this.cardElementTarget)

    // Handle real-time validation errors
    this.cardElement.on('change', (event) => {
      if (event.error) {
        this.showError(event.error.message)
      } else {
        this.hideError()
      }
    })
  }

  async submit(event) {
    event.preventDefault()

    if (!this.stripe || !this.cardElement) {
      this.showError('Payment processor not ready. Please try again.')
      return
    }

    // Disable submit button and show loading
    this.setLoadingState(true)

    try {
      // Create payment method
      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement,
        billing_details: {
          email: this.userEmailValue,
        },
      })

      if (error) {
        this.showError(error.message)
        this.setLoadingState(false)
      } else {
        await this.submitPaymentMethod(paymentMethod)
      }
    } catch (error) {
      this.showError('An unexpected error occurred. Please try again.')
      this.setLoadingState(false)
    }
  }

  async submitPaymentMethod(paymentMethod) {
    try {
      const response = await post('/account/payment_methods', {
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_method: {
            stripe_payment_method_id: paymentMethod.id,
            card_brand: paymentMethod.card.brand,
            card_last_four: paymentMethod.card.last4,
            card_exp_month: paymentMethod.card.exp_month,
            card_exp_year: paymentMethod.card.exp_year
          }
        }),
      })

      if (response.ok) {
        // Redirect to payment methods index
        window.location.href = '/account/payment_methods'
      } else {
        const errorData = await response.json()
        this.showError(errorData.error || 'An error occurred while saving the payment method.')
        this.setLoadingState(false)
      }
    } catch (error) {
      this.showError('An error occurred while saving the payment method.')
      this.setLoadingState(false)
    }
  }

  setLoadingState(loading) {
    this.submitButtonTarget.disabled = loading
    
    if (loading) {
      this.buttonTextTarget.classList.add('hidden')
      this.loadingSpinnerTarget.classList.remove('hidden')
    } else {
      this.buttonTextTarget.classList.remove('hidden')
      this.loadingSpinnerTarget.classList.add('hidden')
    }
  }

  showError(message) {
    this.cardErrorsTarget.textContent = message
    this.cardErrorsTarget.classList.remove('hidden')
  }

  hideError() {
    this.cardErrorsTarget.textContent = ''
    this.cardErrorsTarget.classList.add('hidden')
  }
} 